// src/modules/components/PageTransition/index.js
import React, { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { FaNewspaper } from 'react-icons/fa';

// Assets
import logoVega from '../../../assets/logovega.svg';
import logoCADPro from '../../../assets/logoCADPro.svg';

// ===== ANIMAÇÕES =====
const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const fadeOut = keyframes`
  from { opacity: 1; }
  to { opacity: 0; }
`;

const scaleIn = keyframes`
  from {
    opacity: 0;
    transform: scale(0.8);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
`;

const scaleOut = keyframes`
  from {
    opacity: 1;
    transform: scale(1);
  }
  to {
    opacity: 0;
    transform: scale(1.1);
  }
`;

const slideUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const pulse = keyframes`
  0%, 100% { opacity: 0.4; }
  50% { opacity: 1; }
`;

const lineExpand = keyframes`
  from { width: 0; }
  to { width: 100%; }
`;

const dotPulse = keyframes`
  0%, 80%, 100% {
    transform: scale(0.6);
    opacity: 0.4;
  }
  40% {
    transform: scale(1);
    opacity: 1;
  }
`;

const orbitRotate = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const glowPulse = keyframes`
  0%, 100% {
    box-shadow: 0 0 20px rgba(51, 51, 51, 0.3);
  }
  50% {
    box-shadow: 0 0 40px rgba(51, 51, 51, 0.6), 0 0 60px rgba(51, 51, 51, 0.3);
  }
`;

// ===== STYLED COMPONENTS =====
const TransitionOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, #0a0a0a 0%, #1a1a1a 50%, #0d0d0d 100%);
  z-index: 99999;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  animation: ${props => props.$closing ? fadeOut : fadeIn} 0.5s ease forwards;
`;

const TransitionContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 3rem;
  animation: ${props => props.$closing ? scaleOut : scaleIn} 0.6s ease forwards;
`;

const LogosContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 2rem;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 1.5rem;
  }
`;

const LogoWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.75rem;
  opacity: ${props => props.$active ? 1 : 0.3};
  transform: scale(${props => props.$active ? 1 : 0.9});
  transition: all 0.5s ease;
`;

const LogoCircle = styled.div`
  width: 100px;
  height: 100px;
  border-radius: 50%;
  background: ${props => props.$active ? 'rgba(255, 255, 255, 0.1)' : 'transparent'};
  border: 2px solid ${props => props.$active ? 'rgba(255, 255, 255, 0.2)' : 'rgba(255, 255, 255, 0.05)'};
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  transition: all 0.5s ease;
  animation: ${props => props.$active ? glowPulse : 'none'} 2s ease infinite;

  img {
    height: 40px;
    width: auto;
    filter: ${props => props.$active ? 'brightness(1)' : 'brightness(0.5)'};
    transition: filter 0.5s ease;
  }

  svg {
    font-size: 40px;
    color: ${props => props.$active ? 'rgba(255, 255, 255, 0.9)' : 'rgba(255, 255, 255, 0.5)'};
    transition: color 0.5s ease;
  }

  @media (max-width: 768px) {
    width: 80px;
    height: 80px;

    img {
      height: 32px;
    }

    svg {
      font-size: 32px;
    }
  }
`;

const LogoLabel = styled.span`
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 2px;
  color: ${props => props.$active ? 'rgba(255, 255, 255, 0.9)' : 'rgba(255, 255, 255, 0.3)'};
  transition: color 0.5s ease;
`;

const ConnectionLine = styled.div`
  position: relative;
  width: 120px;
  height: 2px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 1px;
  overflow: hidden;

  @media (max-width: 768px) {
    width: 2px;
    height: 60px;
  }
`;

const ConnectionProgress = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  background: linear-gradient(90deg, #333333, #666666);
  border-radius: 1px;
  animation: ${lineExpand} 1.5s ease forwards;
  animation-delay: 0.5s;
  width: 0;

  @media (max-width: 768px) {
    width: 100%;
    height: 0;
    animation: none;
    height: 100%;
  }
`;

const OrbitRing = styled.div`
  position: absolute;
  width: 130px;
  height: 130px;
  border: 1px dashed rgba(255, 255, 255, 0.1);
  border-radius: 50%;
  animation: ${orbitRotate} 20s linear infinite;

  &::before {
    content: '';
    position: absolute;
    top: -3px;
    left: 50%;
    width: 6px;
    height: 6px;
    background: #666;
    border-radius: 50%;
    transform: translateX(-50%);
  }

  @media (max-width: 768px) {
    width: 110px;
    height: 110px;
  }
`;

const MessageContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  animation: ${slideUp} 0.6s ease forwards;
  animation-delay: 0.3s;
  opacity: 0;
`;

const TransitionTitle = styled.h2`
  font-size: 1.25rem;
  font-weight: 300;
  color: rgba(255, 255, 255, 0.9);
  text-align: center;
  margin: 0;
  letter-spacing: 1px;

  span {
    font-weight: 600;
    color: #fff;
  }

  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

const TransitionSubtitle = styled.p`
  font-size: 0.85rem;
  color: rgba(255, 255, 255, 0.5);
  text-align: center;
  margin: 0;
  max-width: 300px;
  line-height: 1.5;
`;

const LoadingDots = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 1rem;
`;

const Dot = styled.div`
  width: 8px;
  height: 8px;
  background: #666;
  border-radius: 50%;
  animation: ${dotPulse} 1.4s ease-in-out infinite;
  animation-delay: ${props => props.$delay}s;
`;

const ProgressBar = styled.div`
  width: 200px;
  height: 3px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 2px;
  overflow: hidden;
  margin-top: 1.5rem;
`;

const ProgressFill = styled.div`
  height: 100%;
  background: linear-gradient(90deg, #333, #666);
  border-radius: 2px;
  width: ${props => props.$progress}%;
  transition: width 0.3s ease;
`;

const DivisionBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 20px;
  font-size: 0.7rem;
  color: rgba(255, 255, 255, 0.6);
  text-transform: uppercase;
  letter-spacing: 1.5px;
  margin-top: 0.5rem;
  animation: ${pulse} 2s ease infinite;
`;

// ===== COMPONENTE =====
const PageTransition = ({ isVisible, onComplete, targetPage = 'cadpro', skipFadeOut = false }) => {
  const [isClosing, setIsClosing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [activePhase, setActivePhase] = useState(1); // 1 = Vega, 2 = Transition, 3 = CADPro

  // Reset estados quando a transição começa
  useEffect(() => {
    if (isVisible) {
      setIsClosing(false);
      setProgress(0);
      setActivePhase(1);
    }
  }, [isVisible]);

  useEffect(() => {
    if (!isVisible) return;

    // Fase 1: Vega ativa (0-30%)
    const phase1 = setTimeout(() => {
      setActivePhase(1);
    }, 100);

    // Fase 2: Transição (30-70%)
    const phase2 = setTimeout(() => {
      setActivePhase(2);
    }, 800);

    // Fase 3: CADPro ativa (70-100%)
    const phase3 = setTimeout(() => {
      setActivePhase(3);
    }, 1600);

    // Progress bar
    const progressInterval = setInterval(() => {
      setProgress(prev => {
        if (prev >= 100) {
          clearInterval(progressInterval);
          return 100;
        }
        return prev + 2;
      });
    }, 50);

    // Finalizar transição
    const finishTimer = setTimeout(() => {
      // Se skipFadeOut, chama onComplete imediatamente sem fade out
      if (skipFadeOut) {
        onComplete && onComplete();
      } else {
        setIsClosing(true);
        setTimeout(() => {
          onComplete && onComplete();
        }, 500);
      }
    }, 2800);

    return () => {
      clearTimeout(phase1);
      clearTimeout(phase2);
      clearTimeout(phase3);
      clearInterval(progressInterval);
      clearTimeout(finishTimer);
    };
  }, [isVisible, onComplete, skipFadeOut]);

  if (!isVisible) return null;

  const isBlog = targetPage === 'blog';

  // Configurações por tipo de página
  const config = {
    cadpro: {
      targetLabel: 'Engenharia',
      targetLogo: <img src={logoCADPro} alt="CADPro" />,
      phase2Title: <>Acessando divisão de <span>Engenharia</span></>,
      phase3Title: <>Bem-vindo à <span>CADPro</span></>,
      phase2Subtitle: 'Carregando recursos especializados...',
      phase3Subtitle: 'Engenharia Aplicada sob Demanda',
      badge: 'Uma divisão Vega Robotics'
    },
    blog: {
      targetLabel: 'Blog',
      targetLogo: <FaNewspaper />,
      phase2Title: <>Acessando o <span>Blog</span></>,
      phase3Title: <>Bem-vindo ao <span>Blog Vega</span></>,
      phase2Subtitle: 'Carregando artigos e novidades...',
      phase3Subtitle: 'Conteúdo sobre Tecnologia e Inovação',
      badge: 'Blog Vega Robotics'
    }
  };

  const currentConfig = config[targetPage] || config.cadpro;

  return (
    <TransitionOverlay $closing={isClosing}>
      <TransitionContent $closing={isClosing}>
        <LogosContainer>
          <LogoWrapper $active={activePhase >= 1}>
            <LogoCircle $active={activePhase === 1}>
              {activePhase === 1 && <OrbitRing />}
              <img src={logoVega} alt="Vega Robotics" />
            </LogoCircle>
            <LogoLabel $active={activePhase === 1}>Vega Robotics</LogoLabel>
          </LogoWrapper>

          <ConnectionLine>
            <ConnectionProgress />
          </ConnectionLine>

          <LogoWrapper $active={activePhase === 3}>
            <LogoCircle $active={activePhase === 3}>
              {activePhase === 3 && <OrbitRing />}
              {currentConfig.targetLogo}
            </LogoCircle>
            <LogoLabel $active={activePhase === 3}>{currentConfig.targetLabel}</LogoLabel>
          </LogoWrapper>
        </LogosContainer>

        <MessageContainer>
          <TransitionTitle>
            {activePhase === 1 && <>Conectando à <span>Vega Robotics</span></>}
            {activePhase === 2 && currentConfig.phase2Title}
            {activePhase === 3 && currentConfig.phase3Title}
          </TransitionTitle>

          <TransitionSubtitle>
            {activePhase === 1 && 'Iniciando conexão segura...'}
            {activePhase === 2 && currentConfig.phase2Subtitle}
            {activePhase === 3 && currentConfig.phase3Subtitle}
          </TransitionSubtitle>

          <DivisionBadge>
            {currentConfig.badge}
          </DivisionBadge>

          <ProgressBar>
            <ProgressFill $progress={progress} />
          </ProgressBar>

          <LoadingDots>
            <Dot $delay={0} />
            <Dot $delay={0.2} />
            <Dot $delay={0.4} />
          </LoadingDots>
        </MessageContainer>
      </TransitionContent>
    </TransitionOverlay>
  );
};

export default PageTransition;
